import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, inr } from "@/api";
import { PageHeader, EmptyState } from "@/components/Shared";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Landmark } from "lucide-react";

const EMPTY = { name: "", bank: "", account_number: "", ifsc: "", opening_balance: 0, account_type: "bank" };
const selCls = "w-full h-10 rounded-lg border border-slate-300 bg-slate-50/80 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none cursor-pointer px-3 text-sm text-slate-900";

export default function Banks() {
  const { user } = useAuth();
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/banks").then((r) => setBanks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b.account_id);
    setForm({ name: b.name, bank: b.bank || "", account_number: b.account_number || "", ifsc: b.ifsc || "", opening_balance: b.opening_balance || 0, account_type: b.account_type || (b.bank === "Cash" ? "cash" : "bank") });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, opening_balance: Number(form.opening_balance) };
      if (editing) {
        await api.put(`/banks/${editing}`, payload);
        toast.success("Account updated");
      } else {
        await api.post("/banks", payload);
        toast.success("Bank account created");
      }
      setOpen(false); setForm(EMPTY); setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save account");
    } finally { setBusy(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete account ${b.name}?`)) return;
    try {
      await api.delete(`/banks/${b.account_id}`);
      toast.success("Account deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const grand = banks.reduce((s, b) => s + (b.balance || 0), 0);
  const typeOf = (b) => b.account_type || (b.bank === "Cash" ? "cash" : "bank");

  return (
    <div data-testid="banks-page">
      <PageHeader title="Bank & Cash Accounts" sub={`Live balances across all accounts · Total ${inr(grand)}`}>
        <Button onClick={openNew} data-testid="add-bank-btn"
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
          <Plus className="w-4 h-4 mr-1" /> Add Account
        </Button>
      </PageHeader>

      {banks.length === 0 ? (
        <EmptyState icon={Landmark} title="No accounts yet" sub="Create a bank or cash account to track income & expenditure" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {banks.map((b) => (
            <div key={b.account_id} data-testid={`bank-card-${b.account_id}`}
              className="rounded-2xl p-6 bg-white border border-emerald-500/25 hover:border-emerald-400/60 transition-colors duration-300 shadow-[0_0_25px_rgba(16,185,129,0.08)]">
              <div className="flex items-start justify-between mb-5">
                <span className="w-10 h-10 rounded-xl bg-emerald-500/12 border border-emerald-500/35 flex items-center justify-center">
                  <Landmark className="w-5 h-5 text-emerald-600" />
                </span>
                {user?.role === "admin" && (
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(b)} data-testid={`bank-edit-${b.account_id}`}
                      className="text-slate-700 hover:text-emerald-700 px-2 py-1 text-xs rounded-lg hover:bg-slate-700/50 transition-colors duration-150">
                      Edit
                    </button>
                    <button onClick={() => remove(b)} data-testid={`bank-delete-${b.account_id}`}
                      className="text-red-600 hover:text-red-700 p-1 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-slate-900">{b.name}</p>
                <span className={`px-2 py-0.5 rounded-full border text-[10px] font-mono2 uppercase tracking-wider ${typeOf(b) === "cash" ? "border-lime-500/40 text-lime-600 bg-lime-500/10" : "border-blue-500/40 text-blue-600 bg-blue-500/10"}`} data-testid={`bank-type-${b.account_id}`}>
                  {typeOf(b)}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5 font-mono2">
                {b.bank}{b.account_number ? ` · A/c ${b.account_number}` : ""}{b.ifsc ? ` · ${b.ifsc}` : ""}
              </p>
              <p className="font-mono2 font-bold text-2xl text-emerald-600 mt-4" data-testid={`bank-balance-value-${b.account_id}`}>{inr(b.balance)}</p>
              <p className="text-[11px] text-slate-500 mt-1">Opening {inr(b.opening_balance)} · updates live from income, expense & payments</p>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="bank-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Account" : "Add Bank / Cash Account"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            {[["Account Name *", "name", "text", true], ["Bank Name", "bank", "text", false],
              ["Account Number", "account_number", "text", false], ["IFSC", "ifsc", "text", false]].map(([l, k, t, req]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">{l}</Label>
                <Input type={t} required={req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`bank-${k}-input`} className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Account Type</Label>
              <select value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })} data-testid="bank-type-select" className={selCls}>
                <option value="bank">Bank Account</option>
                <option value="cash">Cash Account</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Opening Balance (₹)</Label>
              <Input type="number" step="0.01" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} data-testid="bank-opening-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
            </div>
            <Button type="submit" disabled={busy} data-testid="bank-save-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors duration-200">
              {busy ? "Saving…" : editing ? "Update Account" : "Create Account"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
